import { useEffect, useRef, useState } from 'react'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * リストページの右下に固定表示する「先頭へ戻る」ボタン。
 *
 * スクロールコンテナ (.page) を自身の DOM 位置から `closest('.page')` で特定し、
 * スクロール量がしきい値を超えたときだけ現れる。クリックで先頭まで smooth scroll。
 * vault 一覧 / 緊急アクセス保管庫など、縦に長くなりうるリストで共用する。
 */
const SCROLL_THRESHOLD_PX = 320

export const ScrollTopButton = (): JSX.Element => {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const scrollerRef = useRef<HTMLElement | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const scroller = anchorRef.current?.closest<HTMLElement>('.page') ?? null
    if (scroller === null) return
    scrollerRef.current = scroller

    const onScroll = (): void => {
      setIsVisible(scroller.scrollTop > SCROLL_THRESHOLD_PX)
    }

    onScroll()
    scroller.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      scroller.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <span ref={anchorRef} className="scroll-top-anchor" aria-hidden={!isVisible}>
      {isVisible && (
        <button
          type="button"
          className="scroll-top-btn"
          onClick={() => scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label={t('common.ui.scroll_top')}
          title={t('common.ui.scroll_top')}
        >
          <Icon name="chevron-up" strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </span>
  )
}
